import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import { AlertTriangle } from "lucide-react";

export const HALL_KEYS = ["I", "II", "III", "IV"];

export function countWorkingDays(start, end) {
  if (!start || !end || end < start) return 0;
  let n = 0;
  for (let d = new Date(start + "T00:00:00"); d <= new Date(end + "T00:00:00"); d.setDate(d.getDate() + 1)) {
    if (d.getDay() !== 1 && d.getDay() !== 2) n += 1;
  }
  return n;
}

export function slotDates(id) {
  const m = /^(\d{4})-(\d{2})-w(\d)$/.exec(id || "");
  if (!m) return null;
  const first = new Date(+m[1], +m[2] - 1, 1);
  const wed = new Date(first);
  wed.setDate(1 + ((3 - first.getDay() + 7) % 7) + 7 * (+m[3] - 1));
  const sun = new Date(wed);
  sun.setDate(wed.getDate() + 4);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: iso(wed), end: iso(sun) };
}

const fmt = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function ApprovalSchedule({ app, rates, halls, setHalls, start, setStart, end, setEnd, onConflicts }) {
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!start || !end || end < start || halls.length === 0) { setConflicts([]); onConflicts([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/gallery/conflicts", { params: { start_date: start, end_date: end, halls: halls.join(","), exclude_id: app.id } })
        .then((r) => { setConflicts(r.data.conflicts); onConflicts(r.data.conflicts); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [start, end, halls, app.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHall = (h) => setHalls((cur) => (cur.includes(h) ? cur.filter((x) => x !== h) : HALL_KEYS.filter((k) => k === h || cur.includes(k))));
  const quickFills = (app.preferred_slot_ids || []).map(slotDates).filter(Boolean);

  return (
    <div className="mt-5 space-y-5">
      <div>
        <div className="mb-2 text-xs text-muted2 uppercase tracking-widest">Gallery halls · any combination</div>
        <div className="flex flex-wrap gap-2" data-testid="decision-halls">
          {HALL_KEYS.map((h) => {
            const on = halls.includes(h);
            return (
              <button key={h} type="button" onClick={() => toggleHall(h)} data-testid={`decision-hall-${h}`}
                className={`text-sm px-4 py-2 border transition-colors ${on ? "border-charcoal" : "border-soft hover:bg-cream-2"}`}
                style={on ? { background: "#1C1C19", color: "#F9F8F6" } : {}}>
                Gallery {h} <span className="text-xs opacity-70">· {formatINR(rates[h] || 0)}/day</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block text-xs">
          <div className="mb-1 text-muted2 uppercase tracking-widest">Start date</div>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full bg-transparent border-b border-soft py-2 text-sm" data-testid="decision-start-date" />
        </label>
        <label className="block text-xs">
          <div className="mb-1 text-muted2 uppercase tracking-widest">End date</div>
          <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="w-full bg-transparent border-b border-soft py-2 text-sm" data-testid="decision-end-date" />
        </label>
      </div>

      {quickFills.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <span className="text-muted2">Use preferred:</span>
          {quickFills.map((q, i) => (
            <button key={q.start} type="button" onClick={() => { setStart(q.start); setEnd(q.end); }} className="px-2 py-1 border border-soft hover:bg-cream-2" data-testid={`decision-quickfill-${i}`}>
              {fmt(q.start)} → {fmt(q.end)}
            </button>
          ))}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="border p-3 text-xs space-y-1" style={{ borderColor: "#D4A373", background: "#FFF8E1", color: "#8A6D3B" }} data-testid="decision-conflicts">
          <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={14} /> Overlaps with existing bookings — you can still approve.</div>
          {conflicts.map((c, i) => (
            <div key={i}>{c.reference} · {c.exhibition_title || c.artist_name} · {fmt(c.start_date)} → {fmt(c.end_date)} · Gallery {c.halls.join(", ")} ({c.status})</div>
          ))}
        </div>
      )}
    </div>
  );
}
